import { useContext, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Text, Progress, Box, Flex, Tooltip, Alert, CloseButton } from '@mantine/core';
import { IconCheck, IconChevronRight, IconConfetti, IconStar, IconRocket, IconPlant } from '@tabler/icons-react';
import AuthContext from 'context/AuthContext';
import { useNavigate } from 'react-router-dom';
import routes from 'utils/routes';
import React from 'react';

// Import shared constants or functions if needed
import { IAvailabilityTime, ITeamMember } from 'interfaces/Agent';
import { getOnboardingProgress, OnboardingProgress } from 'services/ApiService';

// Minimum required characters for business information
const BUSINESS_INFO_THRESHOLD = 750;

// Function to get celebration icon based on task type
const getCelebrationIcon = (taskId: string) => {
  if (taskId.includes('business')) {
    return <IconConfetti size={20} color="var(--green-color)" />;
  } else if (taskId.includes('staff')) {
    return <IconStar size={20} color="var(--yellow-color)" />;
  } else if (taskId.includes('message')) {
    return <IconRocket size={20} color="var(--primary-color)" />;
  } else {
    return <IconPlant size={20} color="var(--primary-color)" />;
  }
};

// Notification component that slides out from the setup tracker
interface CustomNotificationProps {
  message: string;
  icon: React.ReactNode;
  onClose: () => void;
  isClosing: boolean;
}

const CustomNotification = ({ message, icon, onClose, isClosing }: CustomNotificationProps): JSX.Element => {
  return (
    <Box
      p="xs" 
      style={{
        position: 'absolute',
        top: '50%', 
        left: '100%',
        width: '250px',
        marginLeft: '10px',
        zIndex: 1000,
        backgroundColor: 'white',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        borderRadius: 'var(--mantine-radius-md)',
        border: '1px solid var(--mantine-color-gray-3)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        transition: 'transform 0.75s ease-in-out, opacity 0.75s ease-in-out',
        transformOrigin: 'left center',
        transform: `translateY(-50%) ${isClosing ? 'scaleX(0)' : 'scaleX(1)'}`,
        opacity: isClosing ? 0 : 1,
        willChange: 'transform, opacity'
      }}
    >
      <Box style={{ flex: '0 0 auto' }}>{icon}</Box>
      <Text fz="sm" style={{ flex: 1 }}>{message}</Text>
      <CloseButton onClick={onClose} />
    </Box>
  );
};

// Setup sections match the ones from dashboard
const setupSections = [
  {
    id: 'training',
    todos: [
      { id: 'business-name', label: 'Business Name' },
      { id: 'welcome-message', label: 'Welcome Message' },
      { id: 'goodbye-message', label: 'Goodbye Message' },
      { id: 'business-info', label: 'Business Information' }
    ]
  },
  {
    id: 'staff',
    todos: [
      { id: 'staff-add', label: 'Add Staff' },
      { id: 'staff-roles', label: 'Add Staff Roles' },
      { id: 'staff-scheduling', label: 'Schedule Staff' }
    ]
  },
  {
    id: 'share',
    todos: [
      { id: 'update-online', label: 'Go Online' },
      { id: 'promote-number', label: 'Promote Your Number' },
      { id: 'social-media', label: 'Share on Social Media' },
      { id: 'email-customers', label: 'Email Your Customers' },
      { id: 'forward-number', label: 'Set Up Call Forwarding' }
    ]
  }
];

// Fun celebration messages for when a task is completed
const getCelebrationMessage = (taskLabel: string) => {
  const messages = [
    `Awesome! You've completed "${taskLabel}"`,
    `Great job setting up "${taskLabel}"`,
    `Nice work! "${taskLabel}" is now complete`,
    `You're making progress! "${taskLabel}" is done`,
    `Another win! "${taskLabel}" is complete`
  ];
  return messages[Math.floor(Math.random() * messages.length)];
};

// Get dynamic title based on progress
const getProgressTitle = (progress: number) => {
  if (progress === 100) {
    return "🎉 Setup Complete";
  } else if (progress >= 67 && progress < 100) {
    return "🚀 Almost There!";
  } else if (progress >= 34 && progress < 67) {
    return "🌱 Making Progress";
  } else {
    return "🔍 Get Started";
  }
};

// Get dynamic description based on progress
const getProgressDescription = (progress: number) => {
  if (progress === 100) {
    return "Your receptionist is fully configured and ready.";
  } else if (progress >= 67 && progress < 100) {
    return "Just a couple more steps to go!";
  } else if (progress >= 34 && progress < 67) {
    return "Keep going - you're doing great!";
  } else {
    return "Start setting up your receptionist.";
  }
};

const SetupProgressTracker = (): JSX.Element => {
  const navigate = useNavigate();
  const { data: { company } } = useContext(AuthContext);
  
  const prevCompletedRef = useRef<{[key: string]: boolean}>({});
  const componentRef = useRef<HTMLDivElement>(null);
  const showTimeoutRef = useRef<number | null>(null);
  const closeTimeoutRef = useRef<number | null>(null);
  const removeTimeoutRef = useRef<number | null>(null);
  
  // Add state for onboarding progress from API
  const [onboardingProgress, setOnboardingProgress] = useState<OnboardingProgress>({ onboarding: {} });
  const [loadingOnboarding, setLoadingOnboarding] = useState(true);
  
  // State to control tracker visibility - default to false, effects will set to true when conditions met
  const [isVisible, setIsVisible] = useState(false); 
  
  // Custom notification state with isVisible flag
  const [notification, setNotification] = useState<{
    message: string;
    icon: React.ReactNode;
    taskId: string;
    isClosing: boolean;
  } | null>(null);

  // Fetch onboarding progress from API
  useEffect(() => {
    const fetchProgress = async () => {
      setLoadingOnboarding(true);
      try {
        const progress = await getOnboardingProgress();
        // console.log('Fetched onboarding progress in tracker:', progress); // Removed log
        // Ensure onboarding object exists
        setOnboardingProgress({
          ...progress,
          onboarding: progress.onboarding || {} // Ensure onboarding exists
        });
      } catch (err) {
        console.error('Failed to load onboarding progress in tracker:', err);
        // Use empty progress object as fallback
        setOnboardingProgress({ onboarding: {} });
      } finally {
        setLoadingOnboarding(false);
      }
    };
    fetchProgress();
    
    // Listen for custom event from dashboard
    const handleProgressUpdate = (event: CustomEvent<OnboardingProgress>) => {
      // console.log('Progress tracker received update event:', event.detail); // Commented out
      setOnboardingProgress({
        ...event.detail,
        onboarding: event.detail.onboarding || {} // Ensure onboarding exists
      });
    };
    
    // Add event listener with type assertion
    document.addEventListener('onboarding-progress-updated', handleProgressUpdate as EventListener);
    
    // Clean up event listener on unmount
    return () => {
      document.removeEventListener('onboarding-progress-updated', handleProgressUpdate as EventListener);
    };
  }, []);
  
  // Wrap isTaskCompleted in useCallback
  const isTaskCompleted = useCallback((taskId: string) => {
    // Safely access the task state from API - handle missing onboarding object
    const taskState = onboardingProgress?.onboarding?.[taskId];
    
    // If user override exists, respect it
    if (taskState?.user_override === 'complete') return true;
    if (taskState?.user_override === 'skipped') return true; // Consider skipped tasks as "completed" for UI
    
    // Otherwise use the actual completion state
    return !!taskState?.completed;
  }, [onboardingProgress]);

  // Wrap isTaskSkipped in useCallback
  const isTaskSkipped = useCallback((taskId: string) => {
    // Safely access the task state - handle missing onboarding object
    const taskState = onboardingProgress?.onboarding?.[taskId];
    
    // Check if it has the 'skipped' user_override (DB value is 'skipped')
    return taskState?.user_override === 'skipped';
  }, [onboardingProgress]);

  // Clear all timeouts to prevent memory leaks - wrap in useCallback
  const clearAllTimeouts = useCallback(() => {
    if (showTimeoutRef.current !== null) {
      window.clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (removeTimeoutRef.current !== null) {
      window.clearTimeout(removeTimeoutRef.current);
      removeTimeoutRef.current = null;
    }
  }, []);

  // Function to close the notification - wrap in useCallback
  const closeNotification = useCallback(() => {
    // Use functional update for setNotification to ensure we have the latest state
    setNotification((prevNotification: any) => {
      if (prevNotification && !prevNotification.isClosing) {
        // Clear any existing timeouts to prevent multiple calls
        if (closeTimeoutRef.current !== null) {
          window.clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
        if (removeTimeoutRef.current !== null) {
          window.clearTimeout(removeTimeoutRef.current);
          removeTimeoutRef.current = null;
        }

        // Set isClosing flag to trigger the exit animation
        const closingNotification = { ...prevNotification, isClosing: true };
        
        // Remove notification from DOM after animation completes
        removeTimeoutRef.current = window.setTimeout(() => {
          setNotification((currentNotification: any) => {
            // Only set to null if it's the same notification that started closing
            if (currentNotification?.taskId === closingNotification.taskId) {
              return null;
            } 
            return currentNotification;
          });
          removeTimeoutRef.current = null;
        }, 750); // Match animation duration
        
        return closingNotification;
      } 
      // If no notification or already closing, return the current state
      return prevNotification;
    });
  }, []);

  // Move showCustomNotification function before its usage and wrap in useCallback
  const showCustomNotification = useCallback((taskId: string, taskLabel: string) => {
    // Clear any existing timeouts
    clearAllTimeouts();

    // Define startAutoCloseTimer inside the callback to avoid dependency issues
    const startAutoCloseTimer = (expectedTaskId: string) => {
      // Clear any existing close timeout first
      if (closeTimeoutRef.current !== null) {
        window.clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      
      // Set new timeout for 5 seconds
      closeTimeoutRef.current = window.setTimeout(() => {
        // Check if the currently displayed notification is the one this timer was for
        setNotification((currentNotification: any) => {
          if (currentNotification && !currentNotification.isClosing && currentNotification.taskId === expectedTaskId) {
            closeNotification(); // Trigger the close sequence
            // The state update will happen within closeNotification
            return currentNotification; // Return current state, closeNotification handles update
          } else {
            return currentNotification;
          }
        });
        closeTimeoutRef.current = null;
      }, 5000); // 5 seconds delay
    };

    const createNewNotification = () => {
      const newNotification = {
        message: getCelebrationMessage(taskLabel),
        icon: getCelebrationIcon(taskId),
        taskId,
        isClosing: false
      };
      setNotification(newNotification);
      startAutoCloseTimer(newNotification.taskId); // Pass taskId to timer
    };

    // If there's already a notification showing, close it first smoothly
    setNotification((prevNotification: any) => {
      if (prevNotification && !prevNotification.isClosing) {
        // Trigger closing animation for the existing one
        const closingNotification = { ...prevNotification, isClosing: true };

        // Set timeout to remove the old one and show the new one
        showTimeoutRef.current = window.setTimeout(() => {
          createNewNotification();
          showTimeoutRef.current = null;
        }, 750); // Wait for closing animation

        // Return the closing state immediately to start the animation
        return closingNotification;
      } else {
        // No existing notification or it's already closing, show the new one after delay
        showTimeoutRef.current = window.setTimeout(() => {
          createNewNotification();
          showTimeoutRef.current = null;
        }, 500); // 500ms delay
        // Return null or the closing state while waiting for the timeout
        return prevNotification; 
      }
    });
  }, [clearAllTimeouts, closeNotification]);

  // Calculate progress and related derived states ONLY when data is loaded
  const { progress, title, description, mappedSectionsCalculated } = useMemo(() => {
    if (loadingOnboarding) {
      // Return default/loading values
      return { progress: 0, title: "Loading...", description: "", mappedSectionsCalculated: [] };
    }

    // Actual calculation logic moved inside useMemo
    const hasBusinessName = Boolean(company?.name);
    const hasWelcomeMessage = Boolean(company?.welcome_message);
    const hasGoodbyeMessage = Boolean(company?.goodbye_message);
    const hasMultipleStaff = (company?.users?.length || 0) > 1;
    const hasStaffWithRoles = company?.users?.some((member: ITeamMember) => Boolean(member.tags?.length));
    const hasScheduledStaff = company?.users?.some((member: ITeamMember) => {
      if (member.status !== 'schedule' || !member.availability) return false;
      const availabilityDays = Object.values(member.availability) as IAvailabilityTime[];
      return availabilityDays.some(day => day.enabled);
    });
    const getTotalChars = () => {
      const cards = company?.cards || [];
      return cards.reduce((total: number, card: { title?: string; body?: string }) => {
        const titleLength = card.title?.length || 0;
        const bodyLength = card.body?.length || 0;
        return total + titleLength + bodyLength;
      }, 0);
    };
    const totalChars = getTotalChars();
    const hasValidBusinessInfo = totalChars >= BUSINESS_INFO_THRESHOLD;

    const sections = setupSections.map((section: any) => ({
      ...section,
      todos: section.todos.map((todo: any) => {
        const apiCompleted = isTaskCompleted(todo.id);
        const apiSkipped = isTaskSkipped(todo.id);
        let uiCompleted = false;
        switch (todo.id) {
          case 'business-name': uiCompleted = hasBusinessName; break;
          case 'welcome-message': uiCompleted = hasWelcomeMessage; break;
          case 'goodbye-message': uiCompleted = hasGoodbyeMessage; break;
          case 'business-info': uiCompleted = hasValidBusinessInfo; break;
          case 'staff-add': uiCompleted = hasMultipleStaff; break;
          case 'staff-roles': uiCompleted = !!hasStaffWithRoles; break;
          case 'staff-scheduling': uiCompleted = !!hasScheduledStaff; break;
          default: uiCompleted = false;
        }
        const isCompleted = uiCompleted || apiCompleted;
        return { 
          ...todo, 
          completed: isCompleted,
          uiCompleted, // Keep for debugging if needed
          apiCompleted, // Keep for debugging if needed
          skipped: apiSkipped
        };
      })
    }));

    const totalTodos = sections.reduce((total, section) => total + section.todos.length, 0);
    const completedTodos = sections.reduce((total, section) => 
      total + section.todos.filter((todo: { completed: boolean }) => todo.completed).length, 0);
    const calculatedProgress = totalTodos > 0 ? Math.round((completedTodos / totalTodos) * 100) : 0;
    const calculatedTitle = getProgressTitle(calculatedProgress);
    const calculatedDescription = getProgressDescription(calculatedProgress);

    return { 
      progress: calculatedProgress, 
      title: calculatedTitle, 
      description: calculatedDescription, 
      mappedSectionsCalculated: sections 
    };
  }, [
    loadingOnboarding,
    company,
    isTaskCompleted,
    isTaskSkipped
  ]);

  // Check for newly completed tasks and show notifications
  useEffect(() => {
    // Skip if still loading
    if (loadingOnboarding) {
      return;
    }

    // Flatten all todos to check completion status
    const allTodos = mappedSectionsCalculated.flatMap((section: any) => section.todos);

    // If the ref is empty, populate it with the current state and return
    // This ensures we only compare against the state from the *previous* run
    if (Object.keys(prevCompletedRef.current).length === 0) {
      allTodos.forEach((todo: any) => {
        prevCompletedRef.current[todo.id] = todo.completed;
      });
      return; // Don't check for notifications on this first run
    }

    // Check each todo to see if it was just completed compared to the ref
    allTodos.forEach((todo: any) => {
      const prevCompleted = prevCompletedRef.current[todo.id] || false;

      const shouldNotify = !prevCompleted && todo.completed && !todo.skipped;

      // If it wasn't completed before but is now, and it's not skipped, show notification
      if (shouldNotify) {
        showCustomNotification(todo.id, todo.label);
      }

      // Update the ref with current completion status *after* comparison
      prevCompletedRef.current[todo.id] = todo.completed;
    });

  }, [
    mappedSectionsCalculated,
    onboardingProgress,
    loadingOnboarding,
    isTaskCompleted,
    isTaskSkipped,
    showCustomNotification
  ]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, [clearAllTimeouts]);

  const handleClick = () => {
    navigate(routes.dashboardPath.href);
  };

  // Handle closing the tracker when progress is 100%
  const handleCloseTracker = () => {
    setIsVisible(false); // Just hide for the current session
  };

  // Effect to handle progress changes (re-opening if progress drops)
  useEffect(() => {
    // Use calculated progress from useMemo, only run when not loading
    if (loadingOnboarding) return;
    
    const currentProgress = progress; // Use derived progress state
    
    // Logic according to user request:
    // 1. Show if progress is < 100%.
    // 2. If progress becomes 100%, it remains visible (state isn't changed here).
    // 3. Visibility is only set to false via handleCloseTracker.
    if (currentProgress < 100) {
      setIsVisible(true);
    } 
    // else: If currentProgress is 100, we don't change isVisible here.
    // It would have been set to true when progress was < 100 previously.
    // It will only become false if the user clicks the close button.
    
  }, [progress, loadingOnboarding]); // Rerun when progress calculation changes

  // Determine color of progress badge
  const getBadgeColor = () => {
    if (progress >= 80) {
      return 'var(--green-color)';
    } else {
      return 'var(--yellow-color)';
    }
  };

  return (
    <Box ref={componentRef} style={{ position: 'relative' }}>
      {!loadingOnboarding && isVisible && (
        <Tooltip 
          label={description}
          position="right"
          withArrow
          color="white"
          styles={{
            tooltip: {
              color: 'var(--text-color)',
              border: '1px solid var(--mantine-color-gray-3)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              padding: '6px 12px',
              fontSize: '14px'
            },
            arrow: {
              border: '1px solid var(--mantine-color-gray-3)',
              background: 'white'
            }
          }}
        >
          <Alert
            radius="md"
            variant="light"
            p="0.75rem"
            onClick={handleClick}
            style={{ 
              cursor: 'pointer', 
              flex: 'auto',
              border: '1px solid',
              transition: 'all 0.2s ease',
              position: 'relative'
            }}
          >
            {progress === 100 && (
              <CloseButton 
                size="sm" 
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  handleCloseTracker(); 
                }} 
                aria-label="Hide setup tracker"
                style={{ 
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  zIndex: 1
                }}
              />
            )}
            
            <Flex direction="column" gap="sm">
              <Text fz="xs" fw={700} c="gray.7" mb={-6}>
                SETUP PROGRESS
              </Text>
              <Flex justify="space-between" align="center">
                <Text fz="sm" fw={500}>
                  {title}
                </Text>
                <Flex align="center" gap={4}>
                  <Text 
                    size="xs" 
                    fw={700} 
                    c={getBadgeColor()}
                    style={{ 
                      background: 'white', 
                      padding: '2px 6px', 
                      borderRadius: '10px',
                      border: `1px solid ${getBadgeColor()}`
                    }}
                  >
                    {progress}%
                  </Text>
                  {progress === 100 ? (
                    <IconCheck size={16} style={{ color: 'var(--green-color)' }} stroke={2.5} />
                  ) : (
                    <IconChevronRight size={16} stroke={1.5} style={{ opacity: 0.7 }} />
                  )}
                </Flex>
              </Flex>
              
              <Progress
                value={progress}
                size="md"
                radius="sm"
                styles={{
                  root: {
                    backgroundColor: 'var(--mantine-color-gray-2)'
                  },
                  section: {
                    backgroundColor: progress >= 80 ? 'var(--green-color)' : 'var(--yellow-color)'
                  }
                }}
              />
            </Flex>
          </Alert>
        </Tooltip>
      )}

      {notification && (
        <CustomNotification
          message={notification.message}
          icon={notification.icon}
          onClose={closeNotification}
          isClosing={notification.isClosing}
        />
      )}
    </Box>
  );
};

export default SetupProgressTracker; 